/**
 * video-editor/media.js — всё, что лента знает о ФАЙЛАХ: что это за файл,
 * сколько длится, есть ли в нём звук, чем его показывать и рисовать.
 *
 * Три рода файлов, одна дверь (общая зона загрузки):
 *   видео — <video> для превью, миниатюры для ленты, частота кадров для «Авто»;
 *   фото  — картинка для превью (уменьшенная) и декод под кадр вывода для файла;
 *   звук  — длительность и волна для дорожки.
 *
 * ⚠️ «Есть ли звук» — из заголовка контейнера (MediaAudioProbe), а не из
 * <video>. До 30.09.2026 тут стояло `webkitAudioDecodedByteCount > 0`, а в
 * Chrome на `loadedmetadata` это всегда 0 — у КАЖДОГО клипа был «нет звука»,
 * и каждый собранный файл выходил немым (осмотр 30.09: h264+aac на входе,
 * одна видеодорожка на выходе, в инспекторе «В этом клипе нет звука»).
 *
 * ⚠️ Файл, который <video> не показывает (AVI/MPEG-4 Part 2, WMV, FLV, 3GP с
 * H.263, HEVC без декодера), нельзя ни просмотреть, ни смонтировать — ни
 * одна из двух половин редактора его не видит. Его сразу приводят к H.264 +
 * AAC через FFmpeg (сперва перепаковка без кодирования, потом кодирование) и
 * дальше работают с копией. Исходный File остаётся в записи (`original`):
 * ключ суточного капа считается по нему, подмена = второе списание.
 */
(function () {
'use strict';
if (window.VideoEditorMedia) return;

var VIDEO_EXT = /\.(mp4|m4v|mov|webm|mkv|avi|wmv|flv|3gp|3g2|ts|mts|m2ts|mpg|mpeg|ogv|vob)$/i;
var IMAGE_EXT = /\.(jpe?g|jfif|png|webp|avif|bmp|gif|heic|heif)$/i;
var AUDIO_EXT = /\.(mp3|wav|m4a|aac|ogg|oga|opus|flac|weba|aif|aiff|amr|wma)$/i;

function kindOf(file) {
    var t = String(file && file.type || '').toLowerCase(), n = String(file && file.name || '');
    if (t === 'image/svg+xml') return null;               // у вектора нет своих пикселей
    if (t.indexOf('video/') === 0 || VIDEO_EXT.test(n)) return 'video';
    if (t.indexOf('image/') === 0 || IMAGE_EXT.test(n)) return 'image';
    if (t.indexOf('audio/') === 0 || AUDIO_EXT.test(n)) return 'audio';
    return null;
}

function once(el, evs, ms) {
    return new Promise(function (resolve) {
        var done = false, to = null;
        function fin(ev) {
            if (done) return; done = true;
            clearTimeout(to);
            evs.forEach(function (e) { el.removeEventListener(e, fin); });
            resolve(ev ? ev.type : 'timeout');
        }
        evs.forEach(function (e) { el.addEventListener(e, fin); });
        to = setTimeout(function () { fin(null); }, ms);
    });
}

function create(opts) {
    opts = opts || {};
    var host = opts.host;                      // скрытый контейнер В DOM: rVFC не стреляет у отвязанного <video>
    var slug = opts.slug || 'video-editor';
    var items = {};
    var seq = 0;
    var listeners = [];
    var thumbQueue = Promise.resolve();

    function emit(m) { listeners.forEach(function (fn) { try { fn(m); } catch (e) {} }); }
    function ae(f, data) { try { window.__ae && window.__ae('feature_use', Object.assign({ slug: slug, f: f }, data || {})); } catch (e) {} }

    function makeVideoEl(url) {
        var v = document.createElement('video');
        v.preload = 'auto';
        v.playsInline = true;
        v.muted = true;
        v.setAttribute('playsinline', '');
        v.crossOrigin = 'anonymous';
        if (host) host.appendChild(v);
        v.src = url;
        return v;
    }

    /* Длительность: у WebM из MediaRecorder в заголовке её нет (Infinity).
       Владелец этого вопроса — timbrica-core.js: элемент в документе он сам
       проходит до конца ДО нашего `loadedmetadata`, а для остального есть
       __mediaLength(el). Своей перемотки в конец здесь нет — второй владелец
       (MediaLengthHasOneOwnerTest). */
    async function finiteDuration(file, v) {
        var d = v.duration;
        if (d > 0 && isFinite(d)) return d;
        if (typeof window.__mediaLength === 'function') {
            try { var s = await window.__mediaLength(v); if (s > 0 && isFinite(s)) return s; } catch (e) {}
        }
        return 0;
    }

    /** Показывает ли <video> этот файл: есть длительность И есть кадр. */
    async function openVideo(file) {
        var url = URL.createObjectURL(file);
        var v = makeVideoEl(url);
        var first = await once(v, ['loadedmetadata', 'error'], 20000);
        if (first !== 'loadedmetadata') { dropEl(v, url); return { ok: false, reason: first === 'error' ? 'decode' : 'timeout' }; }
        // Кадр: MPEG-4 Part 2 / MJPEG / ProRes дают метаданные и 0×0 без ошибки.
        if (v.readyState < 2) await once(v, ['loadeddata', 'error'], 8000);
        if (!(v.videoWidth > 0 && v.videoHeight > 0)) { dropEl(v, url); return { ok: false, reason: 'no-picture' }; }
        var dur = await finiteDuration(file, v);
        if (!(dur > 0)) { dropEl(v, url); return { ok: false, reason: 'no-duration' }; }
        return { ok: true, el: v, url: url, duration: dur, width: v.videoWidth, height: v.videoHeight };
    }
    function dropEl(v, url) {
        try { v.removeAttribute('src'); v.load(); } catch (e) {}
        try { v.remove(); } catch (e) {}
        if (url) { try { URL.revokeObjectURL(url); } catch (e) {} }
    }

    async function audioOf(file, v) {
        var has = null;
        try {
            var P = window.MediaAudioProbe;
            if (P && typeof P.probe === 'function') { var r = await P.probe(file); if (r && typeof r.hasAudio === 'boolean') has = r.hasAudio; }
        } catch (e) {}
        if (has === null && v) {
            if (typeof v.mozHasAudio === 'boolean') has = v.mozHasAudio;
            else if (v.audioTracks && typeof v.audioTracks.length === 'number') has = v.audioTracks.length > 0;
        }
        // Не знаем — считаем, что звук ЕСТЬ: движок сам промолчит на пустой
        // дорожке, а ложное «нет звука» молча выбрасывает настоящий.
        return has === null ? true : has;
    }

    /* Частота кадров — только для «Авто» в выводе; читаем таблицу сэмплов
       (moov / Matroska Tracks), файл целиком не трогаем. */
    async function fpsOf(file) {
        var M = window.VideoMergeWebCodecs;
        if (!M) return 0;
        try {
            var d = null;
            if (M.isMp4Family(file)) { await M.loadMp4Box(); d = await M.demuxLazy(file); }
            else if (M.isMatroska(file)) d = await M.demuxMatroskaLazy(file);
            return d ? Math.round(M.sourceFpsOf(d) * 100) / 100 : 0;
        } catch (e) { return 0; }
    }

    /* Привести непоказываемый файл к H.264 + AAC: перепаковка (секунды, без
       потерь), и только если <video> всё равно не показывает — кодирование. */
    async function convert(rec) {
        rec.state = 'converting'; rec.progress = 0; emit({ type: 'progress', id: rec.id });
        if (typeof window.__ensureFFmpeg === 'function') await window.__ensureFFmpeg();
        var F = window.FFmpegProcessor;
        if (!F || typeof F.processStreaming !== 'function') throw new Error('ffmpeg-unavailable');
        var src = rec.original;
        var scale = "scale='if(gte(iw,ih),min(1920,iw),-2)':'if(gte(iw,ih),-2,min(1920,ih))'";
        var ladder = [
            { how: 'remux', args: ['-map', '0:v:0', '-map', '0:a:0?', '-c', 'copy'] },
            { how: 'remux-aac', args: ['-map', '0:v:0', '-map', '0:a:0?', '-c:v', 'copy', '-c:a', 'aac', '-b:a', '160k', '-ac', '2'] },
            { how: 'encode', args: ['-map', '0:v:0', '-map', '0:a:0?', '-vf', scale, '-c:v', 'libx264', '-preset', 'veryfast', '-crf', '20',
                '-pix_fmt', 'yuv420p', '-c:a', 'aac', '-b:a', '160k', '-ac', '2'] },
        ];
        var t0 = Date.now(), lastErr = null;
        for (var i = 0; i < ladder.length; i++) {
            var step = ladder[i];
            try {
                if (step.how === 'encode') {
                    try { F.onProgress(function (p) { rec.progress = Math.max(0, Math.min(0.99, +p || 0)); emit({ type: 'progress', id: rec.id }); }); } catch (e) {}
                }
                var res = await F.processStreaming(src, step.args, 've-conv-' + rec.id + '.mp4');
                try { F.onProgress(function () {}); } catch (e) {}
                if (!res || !res.blob || !res.blob.size) continue;
                var out = new File([res.blob], String(src.name || 'video').replace(/\.[^.]+$/, '') + '.mp4', { type: 'video/mp4' });
                try { if (res.url) URL.revokeObjectURL(res.url); } catch (e) {}
                var o = await openVideo(out);
                if (!o.ok) { lastErr = new Error('still-unplayable:' + o.reason); continue; }
                ae('media_converted', { how: step.how, ext: extOf(src), ms: Date.now() - t0, mb: Math.round((src.size || 0) / 1048576) });
                return { file: out, open: o, how: step.how };
            } catch (e) {
                lastErr = e;
                try { F.onProgress(function () {}); } catch (x) {}
            }
        }
        ae('media_convert_failed', { ext: extOf(src), err: String(lastErr && lastErr.message || lastErr || '').slice(0, 100) });
        throw lastErr || new Error('convert-failed');
    }
    function extOf(f) { return String((f && f.name) || '').split('.').pop().toLowerCase().slice(0, 5); }

    /* ── миниатюры ────────────────────────────────────────────────────────
       Свой <video> на миниатюры: превью в это время может играть или стоять
       на другом месте, и делить с ним перемотку значит дёргать картинку. */
    var THUMB_H = 96;
    function thumbsFor(rec) {
        thumbQueue = thumbQueue.then(function () { return makeThumbs(rec); }).catch(function () {});
        return thumbQueue;
    }
    async function makeThumbs(rec) {
        if (!rec || rec.kind !== 'video' || !rec.url || rec.disposed) return;
        var n = Math.max(3, Math.min(40, Math.ceil(rec.duration / 2)));
        var v = makeVideoEl(rec.url);
        try {
            if (v.readyState < 2) await once(v, ['loadeddata', 'error'], 10000);
            var w = Math.max(16, Math.round(THUMB_H * (rec.width / Math.max(1, rec.height))));
            var list = [];
            for (var i = 0; i < n; i++) {
                if (rec.disposed) break;
                var t = (i + 0.5) * rec.duration / n;
                if (typeof window.__seekPresented === 'function') await window.__seekPresented(v, t, 2500);
                else { v.currentTime = t; await once(v, ['seeked'], 2500); }
                var c = document.createElement('canvas'); c.width = w; c.height = THUMB_H;
                try { c.getContext('2d').drawImage(v, 0, 0, w, THUMB_H); } catch (e) { continue; }
                list.push({ t: t, c: c });
                if (i === 0 || i === n - 1 || i % 4 === 3) { rec.thumbs = list.slice(); emit({ type: 'thumbs', id: rec.id }); }
            }
            rec.thumbs = list;
            emit({ type: 'thumbs', id: rec.id });
        } finally { dropEl(v, null); }
    }

    /* ── волна звука (дорожки и видеоклипа) ─────────────────────────────────
       Волна — картинка, и ради неё нельзя декодировать файл целиком: 967 МБ
       WAV дали 11 ГБ памяти браузера (/repair-audio, 04.09.2026). Поэтому WAV
       читается потоком по байтам (WaveformPreview.peaksFromPcm — любой длины),
       а сжатый звук и звук видео декодируются целиком только до PEAKS_MAX_SEC;
       длиннее — без волны: дорожка рисуется ровной полосой, работе это не
       мешает. Волны видеоклипов строятся по одной, после миниатюр. */
    var PEAKS_PER_SEC = 40;
    var PEAKS_MAX_SEC = 8 * 60;
    var PEAKS_MAX_MB_UNKNOWN = 40;          // длина неизвестна — судим по размеру
    var peakQueue = Promise.resolve();
    function isWav(f) { return /\.wav$/i.test((f && f.name) || '') || /audio\/(x-)?wav|audio\/wave/i.test((f && f.type) || ''); }
    async function pcmPeaks(rec) {
        var S = window.WavSurgeon, W = window.WaveformPreview;
        if (!S || typeof S.examine !== 'function' || !W || W.__isStub || typeof W.peaksFromPcm !== 'function') return false;
        var d = await S.examine(rec.file);
        if (!d || d.kind !== 'wav' || !d.canRepair || !d.fmt || !d.data) return false;
        var f = d.fmt, tag = f.formatTag === 0xFFFE ? f.subFormat : f.formatTag;
        if (tag !== 1 && tag !== 3) return false;
        var bytes = d.data.actualSize;
        if (d.data.declaredSize > 0 && d.data.declaredSize !== 0xFFFFFFFF) bytes = Math.min(bytes, d.data.declaredSize);
        var block = f.blockAlign || (f.channels * f.bitsPerSample / 8);
        var dur = (block > 0 && f.sampleRate > 0) ? bytes / block / f.sampleRate : 0;
        if (!(dur > 0)) return false;
        var cols = Math.max(1, Math.min(400000, Math.ceil(dur * PEAKS_PER_SEC)));
        var pk = await W.peaksFromPcm(rec.file, { channels: f.channels, sampleRate: f.sampleRate, offset: d.data.offset,
            bytes: bytes, formatTag: tag, bitsPerSample: f.bitsPerSample }, { columns: cols });
        var peaks = new Float32Array(cols);
        for (var i = 0; i < cols; i++) peaks[i] = Math.max(Math.abs(pk.mins[i]), Math.abs(pk.maxs[i]));
        rec.peaks = peaks; rec.peaksPerSec = cols / dur;
        if (!(rec.duration > 0)) rec.duration = dur;
        emit({ type: 'peaks', id: rec.id });
        return true;
    }
    function peaksQueued(rec) {
        peakQueue = peakQueue.then(function () { return rec.disposed ? null : peaksFor(rec); }).catch(function () {});
        return peakQueue;
    }
    async function peaksFor(rec) {
        try {
            if (rec.kind === 'audio' && isWav(rec.file) && await pcmPeaks(rec)) return;
            var sec = rec.duration;
            if (sec > PEAKS_MAX_SEC || (!(sec > 0) && rec.file.size > PEAKS_MAX_MB_UNKNOWN * 1048576)) {
                ae('audio_peaks_skipped', { kind: rec.kind, sec: Math.round(sec || 0), mb: Math.round(rec.file.size / 1048576) });
                return;
            }
            var R = window.AudioDecoderRobust;
            var OAC = window.OfflineAudioContext || window.webkitOfflineAudioContext;
            if (!OAC) return;
            var ctx = new OAC(1, 44100, 44100);
            var buf = null;
            if (R && typeof R.robust === 'function' && !R.__isStub) {
                var r = await R.robust(rec.file, ctx, { slug: slug });
                buf = (r && r.buffer) ? r.buffer : r;
            } else {
                buf = await ctx.decodeAudioData((await rec.file.arrayBuffer()).slice(0));
            }
            if (!buf || !buf.length) return;
            var n = Math.max(1, Math.ceil(buf.duration * PEAKS_PER_SEC));
            var step = buf.length / n, peaks = new Float32Array(n);
            var chs = [];
            for (var c = 0; c < buf.numberOfChannels; c++) chs.push(buf.getChannelData(c));
            for (var i = 0; i < n; i++) {
                var a = Math.floor(i * step), b = Math.min(buf.length, Math.floor((i + 1) * step)), m = 0;
                for (var ch = 0; ch < chs.length; ch++) {
                    var d = chs[ch];
                    for (var j = a; j < b; j += 4) { var x = d[j] < 0 ? -d[j] : d[j]; if (x > m) m = x; }
                }
                peaks[i] = m;
            }
            rec.peaks = peaks; rec.peaksPerSec = PEAKS_PER_SEC;
            if (!(rec.duration > 0)) rec.duration = buf.duration;
            emit({ type: 'peaks', id: rec.id });
        } catch (e) {
            ae('audio_peaks_failed', { ext: extOf(rec.file), err: String(e && e.message || e).slice(0, 80) });
        }
    }

    /* ── фото ─────────────────────────────────────────────────────────── */
    var PREVIEW_LONG = 1600;
    async function openImage(file) {
        var f = file;
        try { if (window.HeicDecode && window.HeicDecode.isHeic(file)) f = await window.HeicDecode.normalize(file); } catch (e) { return { ok: false, reason: 'heic' }; }
        var probe;
        try { probe = await createImageBitmap(f); } catch (e) { return { ok: false, reason: 'decode' }; }
        var w = probe.width, h = probe.height;
        var k = Math.min(1, PREVIEW_LONG / Math.max(w, h));
        var preview = probe;
        if (k < 1) {
            try { preview = await createImageBitmap(probe, { resizeWidth: Math.max(1, Math.round(w * k)), resizeHeight: Math.max(1, Math.round(h * k)), resizeQuality: 'high' }); probe.close(); }
            catch (e) { preview = probe; }
        }
        return { ok: true, file: f, width: w, height: h, bitmap: preview };
    }

    /** Картинка для ФАЙЛА: не больше, чем нужно кадру вывода. */
    async function imageForExport(rec, W, H, fit, rot) {
        var swap = rot === 90 || rot === 270;
        var iw = swap ? rec.height : rec.width, ih = swap ? rec.width : rec.height;
        var k = fit === 'contain' ? Math.min(W / iw, H / ih) : Math.max(W / iw, H / ih);
        if (!(k < 1)) return await createImageBitmap(rec.file);
        return await createImageBitmap(rec.file, {
            resizeWidth: Math.max(1, Math.round(rec.width * k)),
            resizeHeight: Math.max(1, Math.round(rec.height * k)), resizeQuality: 'high' });
    }

    /* ── стоп-кадр ────────────────────────────────────────────────────────
       Кадр исходника на секунде t — в его собственном размере, а не с превью
       960 px: стоп-кадр в файле 1080p/4K должен быть таким же резким, как
       соседние кадры клипа. Свой <video>: элемент превью в это время стоит
       на другом месте, и перематывать его значит дёргать картинку. Поворот
       контейнера кладёт тот же VideoOrientation, что рисует ленту. */
    async function grabFrame(rec, t, name) {
        if (!rec || rec.kind !== 'video' || !rec.url) return null;
        var v = makeVideoEl(rec.url);
        try {
            if (v.readyState < 2) await once(v, ['loadeddata', 'error'], 10000);
            if (typeof window.__seekPresented === 'function') await window.__seekPresented(v, Math.max(0, t), 4000);
            else { v.currentTime = Math.max(0, t); await once(v, ['seeked'], 4000); }
            var VOr = window.VideoOrientation;
            var sz = VOr ? VOr.size(v) : { w: v.videoWidth, h: v.videoHeight };
            if (!(sz.w > 0 && sz.h > 0)) return null;
            var c = document.createElement('canvas');
            c.width = sz.w; c.height = sz.h;
            var g = c.getContext('2d');
            if (VOr) VOr.draw(g, v, 0, 0, sz.w, sz.h); else g.drawImage(v, 0, 0, sz.w, sz.h);
            var blob = await new Promise(function (res) { c.toBlob(res, 'image/jpeg', 0.93); });
            if (!blob || !blob.size) return null;
            return new File([blob], name || 'frame.jpg', { type: 'image/jpeg', lastModified: Date.now() });
        } finally { dropEl(v, null); }
    }

    /* ── вход ──────────────────────────────────────────────────────────── */

    /**
     * Добавить файл. Отдаёт запись сразу (для места на ленте), а готовность —
     * через `rec.ready` (Promise). Отказ — `rec.error` с причиной.
     */
    function add(file) {
        var kind = kindOf(file);
        var rec = {
            id: 'm' + (++seq), kind: kind, name: file && file.name || '', original: file, file: file,
            url: null, el: null, duration: 0, width: 0, height: 0, fps: 0, hasAudio: kind === 'audio',
            thumbs: null, peaks: null, bitmap: null, state: 'probing', progress: 0, error: null, converted: null,
        };
        items[rec.id] = rec;
        rec.ready = probeInto(rec, file);
        return rec;
    }

    /**
     * Файл проекта, чьих байтов нет (большое видео, которое черновик не
     * уносил): запись знает имя, размер и геометрию — лента цела, а превью и
     * экспорт ждут, пока человек выберет этот файл снова (relink).
     */
    function addMissing(desc) {
        var rec = {
            id: 'm' + (++seq), kind: desc.kind, name: desc.name || '', original: null, file: null,
            url: null, el: null, duration: +desc.duration || 0, width: +desc.width || 0, height: +desc.height || 0,
            fps: +desc.fps || 0, hasAudio: desc.hasAudio !== false, thumbs: null, peaks: null, bitmap: null,
            state: 'missing', progress: 0, error: null, converted: null,
            want: { name: desc.name || '', size: +desc.size || 0 },
        };
        items[rec.id] = rec;
        rec.ready = Promise.resolve(rec);
        return rec;
    }
    /** Недостающий файл проекта, которому соответствует выбранный. */
    function findMissing(file) {
        var ids = Object.keys(items);
        for (var i = 0; i < ids.length; i++) {
            var r = items[ids[i]];
            if (r.state === 'missing' && r.want && r.want.name === (file && file.name) && (!r.want.size || r.want.size === file.size)) return r;
        }
        return null;
    }
    /** Вернуть файл недостающей записи: тот же разбор, что у нового файла. */
    function relink(id, file) {
        var rec = items[id];
        if (!rec) return null;
        rec.original = file; rec.file = file; rec.state = 'probing'; rec.error = null; rec.want = null;
        rec.ready = probeInto(rec, file);
        return rec;
    }

    function probeInto(rec, file) {
        var kind = rec.kind;
        return (async function () {
            if (!kind) { rec.state = 'failed'; rec.error = 'kind'; return rec; }
            if (kind === 'video') {
                var o = await openVideo(file);
                if (!o.ok) {
                    rec.state = 'needs-convert'; emit({ type: 'progress', id: rec.id });
                    try {
                        var cv = await convert(rec);
                        rec.file = cv.file; rec.converted = cv.how; o = cv.open;
                    } catch (e) {
                        rec.state = 'failed'; rec.error = 'unplayable';
                        emit({ type: 'failed', id: rec.id });
                        return rec;
                    }
                }
                rec.el = o.el; rec.url = o.url; rec.duration = o.duration; rec.width = o.width; rec.height = o.height;
                rec.hasAudio = await audioOf(rec.file, o.el);
                rec.state = 'ready';
                /* Частота источника — из заголовка, через 2–4 с после добавления
                   (загрузка mp4box + таблица сэмплов). Сборка «как у видео»
                   ДОЖИДАЕТСЯ ответа (fpsSettled), а не берёт 30 по умолчанию:
                   нажатие «Собрать» в первые секунды давало 60-кадровому ролику
                   файл на 30 к/с (стенд video-editor-export-lanes.mjs, 30.10). */
                rec.fpsReady = fpsOf(rec.file).then(function (f) { rec.fps = f; }, function () {});
                // Волна звука клипа — после миниатюр: они нужнее на ленте первыми.
                thumbsFor(rec).then(function () { if (rec.hasAudio && !rec.disposed) peaksQueued(rec); });
            } else if (kind === 'image') {
                var im = await openImage(file);
                if (!im.ok) { rec.state = 'failed'; rec.error = im.reason === 'heic' ? 'heic' : 'image'; emit({ type: 'failed', id: rec.id }); return rec; }
                rec.file = im.file; rec.width = im.width; rec.height = im.height; rec.bitmap = im.bitmap;
                rec.state = 'ready';
            } else {
                var a = document.createElement('audio');
                a.preload = 'auto';
                var aurl = URL.createObjectURL(file);
                if (host) host.appendChild(a);
                a.src = aurl;
                var ev = await once(a, ['loadedmetadata', 'error'], 20000);
                if (ev !== 'loadedmetadata') {
                    try { a.remove(); URL.revokeObjectURL(aurl); } catch (e) {}
                    rec.state = 'failed'; rec.error = 'audio'; emit({ type: 'failed', id: rec.id }); return rec;
                }
                rec.el = a; rec.url = aurl;
                rec.duration = await finiteDuration(file, a);
                rec.state = 'ready';
                // Длину, которую элемент не знал (поток без записанной длины), даёт
                // волна — ленте об этом говорится один раз.
                var knew = rec.duration > 0;
                peaksQueued(rec).then(function () { if (!knew && rec.duration > 0) emit({ type: 'duration', id: rec.id }); });
            }
            emit({ type: 'ready', id: rec.id });
            return rec;
        })().catch(function (e) {
            rec.state = 'failed'; rec.error = rec.error || 'error';
            ae('media_add_failed', { kind: kind || '', err: String(e && e.message || e).slice(0, 100) });
            emit({ type: 'failed', id: rec.id });
            return rec;
        });
    }

    function get(id) { return items[id] || null; }
    function remove(id) {
        var r = items[id];
        if (!r) return;
        r.disposed = true;
        if (r.el) dropEl(r.el, r.url);
        if (r.bitmap && r.bitmap.close) { try { r.bitmap.close(); } catch (e) {} }
        delete items[id];
    }
    function all() { return Object.keys(items).map(function (k) { return items[k]; }); }
    function dispose() { Object.keys(items).forEach(remove); }

    return {
        add: add, addMissing: addMissing, findMissing: findMissing, relink: relink,
        get: get, remove: remove, all: all, dispose: dispose,
        on: function (fn) { listeners.push(fn); },
        imageForExport: imageForExport,
        grabFrame: grabFrame,
        /** Частоты источников этих записей известны (или не будут известны
            вовсе) — не дольше `ms`. */
        fpsSettled: function (ids, ms) {
            var ps = (ids || []).map(function (id) { var r = items[id]; return r && r.fpsReady; }).filter(Boolean);
            if (!ps.length) return Promise.resolve();
            return Promise.race([Promise.all(ps), new Promise(function (res) { setTimeout(res, ms > 0 ? ms : 8000); })]);
        },
        makeVideoEl: makeVideoEl,
        kindOf: kindOf,
    };
}

window.VideoEditorMedia = { create: create, kindOf: kindOf };
})();
